export interface BossOptions {
  id: string;
  name: string;
  maxHp: number;
  attackIntervalSec?: number;
  telegraphDurationSec?: number;
  defense?: number;
  position?: { x: number; y: number; z: number };
}

export interface BossHitResult {
  currentHp: number;
  phaseChanged: boolean;
  defeated: boolean;
}

export interface BossTickResult {
  state: string;
  phase: number;
  telegraphTimer: number;
  isTelegraphing: boolean;
  attackTriggered: boolean;
}

export class BossEncounter {
  public readonly id: string;
  public readonly name: string;
  public readonly maxHp: number;
  public currentHp: number;
  public defense: number;
  public phase: number = 1;
  public position: { x: number; y: number; z: number };

  private attackTimer: number = 0;
  private attackIntervalSec: number;
  private telegraphDurationSec: number;

  constructor(options: BossOptions) {
    this.id = options.id;
    this.name = options.name;
    this.maxHp = options.maxHp;
    this.currentHp = options.maxHp;
    this.defense = options.defense ?? 15;
    this.attackIntervalSec = options.attackIntervalSec ?? 4.0;
    this.telegraphDurationSec = options.telegraphDurationSec ?? 1.5;
    this.position = options.position ? { ...options.position } : { x: 0, y: 0, z: 12.0 };
  }

  public get isEnraged(): boolean {
    return this.phase >= 2;
  }

  public get isDefeated(): boolean {
    return this.currentHp <= 0;
  }

  public takeDamage(amount: number): BossHitResult {
    if (this.isDefeated) {
      return { currentHp: 0, phaseChanged: false, defeated: true };
    }

    this.currentHp = Math.max(0, this.currentHp - amount);

    let phaseChanged = false;
    if (this.phase === 1 && this.currentHp <= this.maxHp * 0.5) {
      this.phase = 2;
      phaseChanged = true;
      this.attackIntervalSec *= 0.75;
      this.telegraphDurationSec *= 0.8;
    }

    return {
      currentHp: this.currentHp,
      phaseChanged,
      defeated: this.isDefeated
    };
  }

  public getDropLoot() {
    if (!this.isDefeated) return null;
    return {
      itemId: 'golem_core',
      name: 'Resonant Crystal Core',
      massKg: 5.0,
      type: 'mineral' as const
    };
  }

  public update(dt: number): BossTickResult {
    if (this.isDefeated) {
      return {
        state: 'defeated',
        phase: this.phase,
        telegraphTimer: 0,
        isTelegraphing: false,
        attackTriggered: false
      };
    }

    this.attackTimer += dt;
    const telegraphStart = Math.max(0, this.attackIntervalSec - this.telegraphDurationSec);
    const isTelegraphing = this.attackTimer >= telegraphStart;

    if (this.attackTimer >= this.attackIntervalSec) {
      this.attackTimer = 0;
      return {
        state: 'attacking',
        phase: this.phase,
        telegraphTimer: 0,
        isTelegraphing: false,
        attackTriggered: true
      };
    }

    return {
      state: isTelegraphing ? 'telegraphing' : 'idle',
      phase: this.phase,
      telegraphTimer: isTelegraphing ? this.attackTimer - telegraphStart : 0,
      isTelegraphing,
      attackTriggered: false
    };
  }
}
